import { computeWindowedPnl, type PnlHandLike } from "../history/pnl.js";

interface PnlGraphProps {
  /** Full match history, oldest first, spanning all matches (not capped). */
  hands: readonly PnlHandLike[];
  /** Most-recent hands to plot; the cumulative baseline still carries in from before this window. */
  windowSize?: number;
}

const HEIGHT = 150;
const WIDTH = 300;

function fmtBb(v: number): string {
  const rounded = Math.round(v);
  return `${rounded > 0 ? "+" : ""}${rounded}`;
}

/**
 * Cumulative net-bb line chart (actual vs. all-in-adjusted EV), for the
 * history tab (docs/UI_REFERENCE.md "フィードバック反映"). Plain inline
 * SVG, no charting library. Axis labels/ticks are plain HTML overlaid on
 * the SVG (not SVG `<text>`) because the chart uses
 * `preserveAspectRatio="none"` to stretch the plot to fill the container
 * width, which would non-uniformly distort SVG text.
 */
export function PnlGraph({ hands, windowSize = 100 }: PnlGraphProps) {
  if (hands.length === 0) return null;

  const { actual: actualPoints, ev: evPoints, handNumbers } = computeWindowedPnl(hands, windowSize);
  const n = actualPoints.length;
  if (n === 0) return null;

  const allValues = [0, ...actualPoints, ...evPoints];
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min || 1;
  const xStep = n > 1 ? WIDTH / (n - 1) : 0;

  function toXY(points: readonly number[]): string {
    return points
      .map((v, i) => {
        const x = n > 1 ? i * xStep : WIDTH / 2;
        const y = HEIGHT - ((v - min) / range) * HEIGHT;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  }

  const zeroY = HEIGHT - ((0 - min) / range) * HEIGHT;
  const zeroYPct = (zeroY / HEIGHT) * 100;

  return (
    <div className="pnl-graph">
      <div className="pnl-graph-frame">
        {/* Y-axis ticks are scoped to this plot wrapper (matching the SVG's
            own height) rather than the outer frame (which also includes
            the frame's bottom padding strip reserved for the X-axis
            labels below) -- previously a `top: 100%` y-tick (e.g. the "0"
            line sitting near the very bottom of the chart) resolved
            against the *frame's* full height, landing inside that same
            bottom strip and colliding with the "#1" x-axis label
            (2026-07-17 フィードバック #4 -- screenshot showed "#1"/"0"
            overlapping at the graph's bottom-left corner). */}
        <div className="pnl-graph-plot">
          <svg
            className="pnl-graph-svg"
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            role="img"
            aria-label="累積収支グラフ"
          >
            <line x1={0} x2={WIDTH} y1={zeroY} y2={zeroY} className="pnl-zero-line" vectorEffect="non-scaling-stroke" />
            <polyline
              points={toXY(evPoints)}
              className="pnl-line pnl-line-ev"
              vectorEffect="non-scaling-stroke"
            />
            <polyline
              points={toXY(actualPoints)}
              className="pnl-line pnl-line-actual"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          {/* Y axis value ticks: 0-line and min/max, scoped to the plot's
              own height (see the comment above). */}
          <span className="pnl-tick-y pnl-tick-y-max" style={{ top: 0 }}>
            {fmtBb(max)}
          </span>
          <span className="pnl-tick-y pnl-tick-y-zero" style={{ top: `${zeroYPct}%` }}>
            0
          </span>
          <span className="pnl-tick-y pnl-tick-y-min" style={{ top: "100%" }}>
            {fmtBb(min)}
          </span>
        </div>

        {/* Y axis unit label: stays a direct child of the outer frame (not
            the plot wrapper above) -- it sits in the frame's own
            padding-left gutter, to the left of the plot, unaffected by the
            y-tick scoping fix. */}
        <span className="pnl-axis-label pnl-axis-label-y">BB</span>

        {/* X axis: unit label + first/last plotted hand numbers (numbered
            across the full history, per computeWindowedPnl). Positioned
            relative to the outer frame, in its bottom padding strip --
            below the plot wrapper above, not overlapping it. */}
        <span className="pnl-tick-x pnl-tick-x-start" style={{ left: 0 }}>
          #{handNumbers[0]}
        </span>
        <span className="pnl-tick-x pnl-tick-x-end" style={{ left: "100%" }}>
          #{handNumbers[n - 1]}
        </span>
      </div>
      <div className="pnl-axis-label pnl-axis-label-x">ハンド</div>
      <div className="pnl-legend">
        <span className="pnl-legend-item">
          <span className="pnl-swatch pnl-swatch-actual" />
          収支
        </span>
        <span className="pnl-legend-item">
          <span className="pnl-swatch pnl-swatch-ev" />
          収支EV
        </span>
      </div>
    </div>
  );
}
